import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

interface BookingCalendarItem {
  id: number;
  bookingNumber?: string;
  userName?: string;
  gymName?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  status: string;
  amount?: number;
}

interface BookingCalendarViewProps {
  bookings: BookingCalendarItem[];
  onSelectBooking?: (booking: BookingCalendarItem) => void;
}

export const BookingCalendarView: React.FC<BookingCalendarViewProps> = ({
  bookings,
  onSelectBooking
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<BookingCalendarItem[] | null>(null);
  const [showDayDetails, setShowDayDetails] = useState(false);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getBookingsForDate = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.date === dateStr);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      confirmed: '#3B82F6',
      'checked-in': '#F59E0B',
      completed: '#10B981',
      cancelled: '#EF4444',
      pending: '#14B8A6'
    };
    return colors[status] || '#6B7280';
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDayClick = (day: number) => {
    const dayBookings = getBookingsForDate(day);
    setSelectedDay(dayBookings);
    setShowDayDetails(true);
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Days of month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Box>
      {/* Calendar Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handlePrevMonth} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 'bold', minWidth: 200, textAlign: 'center' }}>
              {monthName}
            </Typography>
            <IconButton onClick={handleNextMonth} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Day Headers */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <Grid item xs={12 / 7} key={day}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    color: '#666',
                    py: 1
                  }}
                >
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Days */}
          <Grid container spacing={1}>
            {days.map((day, index) => {
              const dayBookings = day ? getBookingsForDate(day) : [];
              const hasBookings = dayBookings.length > 0;

              return (
                <Grid item xs={12 / 7} key={index}>
                  <Box
                    onClick={() => day && handleDayClick(day)}
                    sx={{
                      p: 1,
                      minHeight: 100,
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      backgroundColor: hasBookings ? '#f0fffe' : '#fff',
                      cursor: day ? 'pointer' : 'default',
                      '&:hover': day && hasBookings ? { backgroundColor: '#e0f7f6' } : {},
                      transition: 'all 0.2s'
                    }}
                  >
                    {day && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            mb: 1,
                            color: hasBookings ? '#14B8A6' : '#999'
                          }}
                        >
                          {day}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {dayBookings.slice(0, 2).map(booking => (
                            <Chip
                              key={booking.id}
                              label={booking.bookingNumber}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(booking.status) + '20',
                                color: getStatusColor(booking.status),
                                fontSize: '0.65rem',
                                height: 20
                              }}
                            />
                          ))}
                          {dayBookings.length > 2 && (
                            <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                              +{dayBookings.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Day Details Dialog */}
      <Dialog open={showDayDetails} onClose={() => setShowDayDetails(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bookings for {selectedDay && selectedDay.length > 0
            ? new Date(selectedDay[0].date || '').toLocaleDateString()
            : 'Selected Date'}
        </DialogTitle>
        <DialogContent>
          {selectedDay && selectedDay.length > 0 ? (
            <Box sx={{ pt: 2 }}>
              {selectedDay.map(booking => (
                <Card key={booking.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {booking.bookingNumber}
                      </Typography>
                      <Chip
                        label={booking.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(booking.status) + '20',
                          color: getStatusColor(booking.status)
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {booking.userName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {booking.gymName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      {booking.startTime} - {booking.endTime}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                      ₹{booking.amount}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999', pt: 2 }}>
              No bookings for this date
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDayDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingCalendarView;
